<script lang="ts">
	import type { ProductCardMissingInfo$result } from '$houdini';
	import Image from '$lib/components/Image/Image.svelte';
	import ProductPrice from '$lib/components/Product/ProductPrice.svelte';
	import ProductPriceRange from '$lib/components/Product/ProductPriceRange.svelte';
	import ProductCardSelector from '$lib/components/Product/Card/ProductCardSelector.svelte';

	import {
		getPriceAfterDiscount,
		formatProductTitle,
		mapToNode,
		transformProductCollectionsToBranch
	} from '$lib/utils/product';
	import { parseImageUrls } from '$lib/utils/image';
	import { resolveRoute } from '$app/paths';
	import { page } from '$app/stores';
	import slug from 'slug';
	import { getCollectionPath } from '$lib/utils/collectionTree';

	export let data: ProductCardMissingInfo$result;
	export let sku: string | undefined;
	export let discountedPercentage: number;

	$: selectedVariant = mapToNode(data.product?.variants).find((s) => s.sku?.value === sku);
	$: collectionPath =
		getCollectionPath(data.product?.collections)?.[0].url_key ||
		transformProductCollectionsToBranch(data.product?.collections) ||
		'hund';

	$: parsedImageUrls =
		(selectedVariant
			? parseImageUrls(selectedVariant.imageUrls) ?? parseImageUrls(data.product?.imageUrls)
			: parseImageUrls(data.product?.imageUrls)) ?? [];

	$: priceData = (() => {
		if (selectedVariant) {
			return {
				price: getPriceAfterDiscount(selectedVariant.price, discountedPercentage),
				compareAtPrice: selectedVariant.compareAtPrice
			};
		}

		return {
			priceRange: {
				minVariantPrice: getPriceAfterDiscount(
					data.product?.priceRange?.minVariantPrice,
					discountedPercentage
				),
				maxVariantPrice: getPriceAfterDiscount(
					data.product?.priceRange?.maxVariantPrice,
					discountedPercentage
				)
			},
			compareAtPriceRange: {
				minVariantPrice: data.product?.compareAtPriceRange?.minVariantPrice,
				maxVariantPrice: data.product?.compareAtPriceRange?.maxVariantPrice
			}
		};
	})();

	$: href = selectedVariant
		? resolveRoute(
				`/[locale]-[country]/[branch=branch]/[slug]/[handle]?variantSKU=${selectedVariant.sku?.value || ''}`,
				{
					...$page.params,
					slug: slug(data?.product?.title || ''),
					branch: collectionPath,
					handle: data.product?.handle
				}
			)
		: resolveRoute('/[locale]-[country]/[branch=branch]/[slug]/[handle]/', {
				...$page.params,
				slug: slug(data?.product?.title || ''),
				branch: collectionPath,
				handle: data.product?.handle
			});
</script>

<div class="flex gap-4">
	<a {href} class="flex-none">
		<Image
			src={parsedImageUrls[0]}
			alt=""
			width={256}
			height={256}
			class="w-32 h-32"
			sizes="(min-width: 768px) 5vw, 18vw"
			shouldShowLoad
		/>
	</a>
	<div class="flex flex-col w-full">
		<a {href}>
			<h5>{formatProductTitle(data.product?.title || '', selectedVariant?.title)}</h5>
			{#if selectedVariant}
				<ProductPrice price={priceData.price} compareAtPrice={priceData.compareAtPrice} />
			{:else if priceData.priceRange && priceData.compareAtPriceRange}
				<ProductPriceRange
					priceRange={priceData.priceRange}
					compareAtPriceRange={priceData.compareAtPriceRange}
				/>
			{/if}
		</a>
		<div class="mt-4"></div>
		<ProductCardSelector {data} bind:sku on:complete on:incomplete distinctColorAndOptions />
	</div>
</div>
